/**
 * Phone number formatting utilities using libphonenumber-js
 */
import { parsePhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

/**
 * Format a phone number for display.
 * 
 * @param phone - The phone number to format (can include country code or not)
 * @param defaultCountry - Default country code if phone doesn't include one (default: 'BR')
 * @returns Formatted phone number or original string if parsing fails
 * 
 * @example
 * formatPhone('+5511999999999') // '+55 11 99999-9999'
 * formatPhone('11999999999', 'BR') // '+55 11 99999-9999'
 * formatPhone('999999999', 'BR') // '999999999' (too short, returns as-is)
 * formatPhone(null) // ''
 */
export function formatPhone(phone: string | null | undefined, defaultCountry: CountryCode = 'BR'): string {
  if (!phone) return '';
  
  // Clean the phone number (keep + and digits only)
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  if (!cleaned) return phone;
  
  try {
    const parsed = parsePhoneNumber(cleaned, defaultCountry);
    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }
  } catch {
    // Parsing failed, try with + prefix if it looks like it has country code
  }
  
  // If parsing failed but phone starts with country code, try formatting manually
  if (cleaned.startsWith('+')) {
    try {
      const parsed = parsePhoneNumber(cleaned);
      if (parsed) {
        return parsed.formatInternational();
      }
    } catch {
      // Still failed, return original
    }
  }
  
  return phone;
}

/**
 * Format a phone number in national format (without country code).
 * 
 * @param phone - The phone number to format
 * @param defaultCountry - Default country code (default: 'BR')
 * @returns Formatted phone number in national format
 * 
 * @example
 * formatPhoneNational('+5511999999999') // '(11) 99999-9999'
 * formatPhoneNational('11999999999', 'BR') // '(11) 99999-9999'
 */
export function formatPhoneNational(phone: string | null | undefined, defaultCountry: CountryCode = 'BR'): string {
  if (!phone) return '';
  
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  if (!cleaned) return phone;
  
  try {
    const parsed = parsePhoneNumber(cleaned, defaultCountry);
    if (parsed && parsed.isValid()) {
      return parsed.formatNational();
    }
  } catch {
    // Parsing failed
  }
  
  return phone;
}

/**
 * Normalize a phone number to E.164 format when possible.
 *
 * @example
 * normalizePhoneE164('11 99999-9999') // '+5511999999999'
 * normalizePhoneE164('+5511999999999') // '+5511999999999'
 */
export function normalizePhoneE164(
  phone: string | null | undefined,
  defaultCountry: CountryCode = 'BR'
): string {
  if (!phone) return '';

  const cleaned = phone.replace(/[^\d+]/g, '');
  if (!cleaned) return '';

  try {
    const parsed = parsePhoneNumber(cleaned, defaultCountry);
    if (parsed && parsed.isValid()) {
      return parsed.number;
    }
  } catch {
    // Fall through to manual normalization
  }

  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  if (cleaned.length === 11 || cleaned.length === 10) {
    return `+55${cleaned}`;
  }

  return `+${cleaned}`;
}
